/**
 * `createApiExplorerSource` — the explorer's WRITE port over the shared
 * `ApiClient`.
 *
 * The transaction explorer READS through `@authe/analytics-widgets` hooks
 * (catalog / stream / facet-values, via ui-core `useQuery`); this port is only
 * the mutations that turn browsing into durable structure: promote a browsed
 * field to an indexed **facet** (`POST /metadata-facets`), assemble facets into
 * a **view** (`POST /metadata-views`), and build authored views into physical
 * `mv_*` relations (`POST /metadata-analytics/materialize`). Bodies + the
 * `?scope=` rung are assembled by the framework-agnostic builders in
 * `@authe/fe-commons/metadata-analytics`, so the wire contract lives in exactly
 * one place. Canonical `/metadata-*` paths are rewritten to
 * `internal/metadata-registry-api` by `RESOURCE_REWRITES`.
 */
import { unwrapData, type ApiClient } from "@authe/fe-commons/api";
import {
  type CatalogField,
  configPaths,
  facetCreateBody,
  materializeRequest,
  scopeQuery,
  viewCreateBody,
} from "@authe/fe-commons/metadata-analytics";

/** Ladder rung a write authors at (mirrors the backend `FacetVisibility`). */
export type WriteScope = "customer" | "org" | "tenant" | "user";

export interface CreateViewInput {
  viewId: string;
  facetNames: string[];
  source?: string;
  protocolId?: string;
  groupBy?: string[];
  scope?: WriteScope;
}

export interface ExplorerSource {
  /**
   * Promote a browsed field to a durable indexed facet, at `scope`.
   *
   * REQUIRED, with no fallback here on purpose. Promoting a field is an authored
   * registry overlay, so which rung it lands on is the caller's configured
   * "Default visibility" — and a `?? "tenant"` in this adapter is how that
   * setting was silently ignored: the only call site passed nothing, so every
   * facet went tenant-wide whatever the operator had chosen. A required
   * parameter turns the next such caller into a compile error rather than a
   * quiet widening.
   */
  createFacet(field: CatalogField, scope: WriteScope): Promise<void>;
  /** Assemble facets into a materialized-view spec (default rung: tenant). */
  createView(input: CreateViewInput): Promise<void>;
  /** Build authored views into physical `mv_*` relations (one, or all in scope). */
  materialize(viewId?: string): Promise<void>;
}

export function createApiExplorerSource(client: ApiClient): ExplorerSource {
  return {
    async createFacet(field: CatalogField, scope: WriteScope): Promise<void> {
      unwrapData(await client.post(configPaths.facets(), facetCreateBody(field, { visibility: scope }), {
        query: scopeQuery(scope),
      }));
      // The catalog read is what surfaces a facet, and it is the only read that
      // does: `/metadata-facets` itself is written here and read nowhere, so the
      // `client.invalidate(configPaths.facets())` that used to sit beside this
      // line dropped a key no read had ever written.
      client.invalidateMatching("/metadata-analytics/catalog");
    },

    async createView(input: CreateViewInput): Promise<void> {
      const body = viewCreateBody({
        viewId: input.viewId,
        facetNames: input.facetNames,
        scope: input.scope,
        source: input.source,
        protocolId: input.protocolId,
        groupBy: input.groupBy,
      });
      unwrapData(await client.post(configPaths.views(), body, { query: scopeQuery(input.scope) }));
      // Nothing to invalidate, and the asymmetry with `createFacet` above is
      // real rather than an omission: an authored view spec has NO read surface
      // in this front end (`/metadata-views` is POSTed here and fetched
      // nowhere), and creating one changes no catalog answer until
      // `materialize` builds the physical `mv_*` relation.
    },

    async materialize(viewId?: string): Promise<void> {
      const req = materializeRequest(viewId);
      unwrapData(await client.post(req.path, {}, { query: req.query }));
    },
  };
}
